/*
 * Copyright (c) 2019. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import _has from "lodash/has";
import _pick from "lodash/pick";
import _merge from "lodash/merge";
import { applicationStatuses } from "./applicationStatus";
import formSteps from "./formSteps";
import ApiClient from "../apiClient";
import _cloneDeep from "lodash/cloneDeep";
import _forEach from "lodash/forEach";
import _set from "lodash/set";
import _get from "lodash/get";
import { fromInsurance, sanitizePersonData, toInsurance } from "../formServiceFunctions";
import { getProductData } from "../productService";
import { FormServiceBase, FormServiceSaveProps } from "../../types/FormServiceBase";
import { AbstractCase } from "../../types/Entities/AbstractCase";
import { Customer } from "../../types/Entities/Customer";

type ApplicationStatus = typeof applicationStatuses[keyof typeof applicationStatuses];

class FormService extends FormServiceBase<ApplicationStatus> {
  dataFields = (): { [k in ApplicationStatus]?: { [key: string]: string[] } } => ({
    [applicationStatuses.APPLICATION_STATUS_CATEGORY]: {
      death: ["generalCategory", "deathCategory", "inheritanceCategory"],
    },
    [applicationStatuses.APPLICATION_STATUS_DESCRIPTION]: {
      death: ["personalTextMessage"],
    },
    [applicationStatuses.APPLICATION_STATUS_DOCUMENTS]: {
      death: [],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTACT]: {
      client: ["gender", "givenName", "familyName"],
      customer: ["telephone"],
      insurance: ["insurancePolicyNumber", "caseReferenceNumber"],
    },
    [applicationStatuses.APPLICATION_STATUS_INSURANCE]: {
      insurance: ["insurance", "caseReferenceNumber", "insurancePolicyNumber", "policyHolderType"],
      "insurance.policyHolder": ["gender", "givenName", "familyName"],
    },
  });

  conversionFields = [
    {
      path: "insurance.insurance",
      convertFrom: fromInsurance,
      convertTo: toInsurance,
    },
  ];

  save = async ({ formData, applicationStatus, nextApplicationStatus, product, customer, updateMutation }: FormServiceSaveProps<ApplicationStatus>) => {
    const fields = this.dataFields()[applicationStatus] || {};

    if (_has(fields, "customer")) {
      const customerData = _pick(formData.customer, fields.customer);
      await updateMutation.mutateAsync({
        uri: "/customers",
        id: customer.id,
        data: customerData,
      });
    }

    if (_has(fields, "client")) {
      let clientData = _pick(formData.client, fields.client);
      clientData = sanitizePersonData(clientData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.client.id,
        data: clientData,
      });
    }

    if (_has(fields, "insurance")) {
      const insuranceData = _pick(formData.insurance, fields.insurance);
      await ApiClient.put("/customer_insurances/" + product.insurance.id, { body: JSON.stringify(insuranceData) });
    }

    let deathData: Partial<AbstractCase> = {};
    if (nextApplicationStatus) {
      deathData.applicationStatus = nextApplicationStatus;
      this.statusFunctions[applicationStatus]?.();
    }

    if (_has(fields, "death")) {
      deathData = this.transformData(_merge(deathData, _pick(formData, fields.death)));
    }
    await updateMutation.mutateAsync({ uri: getProductData(product.name, "apiUri"), id: product.id, data: deathData });
  };

  transformData = (data: Partial<AbstractCase>) => {
    let resultData = _merge({}, data);
    _forEach(this.conversionFields, (conversionField) => {
      const { path, convertTo } = conversionField;
      if (_has(resultData, path)) {
        _set(resultData, path, convertTo(_get(resultData, path)));
      }
    });
    return resultData;
  };

  prepareFormData = (product: AbstractCase, customer: Customer) => {
    let resultData = _cloneDeep(product);
    resultData.customer = _cloneDeep(customer);
    _forEach(this.conversionFields, (conversionField) => {
      const { path, convertFrom } = conversionField;
      if (_has(resultData, path)) {
        _set(resultData, path, convertFrom(_get(resultData, path)));
      }
    });
    return resultData;
  };

  getAvailableSteps = () => {
    let availableSteps = [];

    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CATEGORY]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DESCRIPTION]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DOCUMENTS]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTACT]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INSURANCE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_SUMMARY]);

    return availableSteps;
  };
}

export default new FormService();
